import {
  DayOfWeek,
  DirectionalSpacing,
  FlexItems,
  FlexJustify,
  Opacity,
  ResponsiveValue,
} from "@/types/core";
import { clsx, type ClassValue } from "clsx";
import dayjs from "dayjs";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function getColors({
  color = "primary",
  opacity,
  type = "bg",
}: {
  color: string;
  opacity?: Opacity;
  type?: "bg" | "text";
}) {
  const textColors: Record<string, string> = {
    primary: "text-primary",
    secondary: "text-secondary",
    accent: "text-accent",
    white: "text-white",
    black: "text-black",
    red: "text-red",
    green: "text-green",
    blue: "text-blue",
    yellow: "text-yellow",
    orange: "text-orange",
    pink: "text-pink",
    purple: "text-purple",
    gray: "text-gray",
    brown: "text-brown",
    border: "border",
  };

  const bgColors: Record<string, string> = {
    primary: "bg-primary",
    secondary: "bg-secondary",
    accent: "bg-accent",
    white: "bg-white",
    black: "bg-black",
    red: "bg-red",
    green: "bg-green",
    blue: "bg-blue",
    yellow: "bg-yellow",
    orange: "bg-orange",
    pink: "bg-pink",
    purple: "bg-purple",
    gray: "bg-gray",
    brown: "bg-brown",
    border: "border",
  };

  const colorValue = color.toLocaleLowerCase();
  const opacityValue = opacity ? `/${opacity}` : "";

  return (
    (type === "text" ? textColors[colorValue] : bgColors[colorValue]) +
    opacityValue
  );
}

const FILE_PATH = process.env.NEXT_PUBLIC_FILE_PREFFIX!;

export function getFilePath(key: string) {
  return `${FILE_PATH}/${key}`;
}

export function getOpacity(opacity: Opacity) {
  return `opacity-${opacity}`;
}

export function getFlexStrecth(
  size: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | "auto",
) {
  return `flex-${size}`;
}

export function getFlexJustify(justify: FlexJustify) {
  return `justify-${justify}`;
}

export function getFlexItems(items: FlexItems) {
  return `items-${items}`;
}

export function getSpacing({
  size,
  type,
}: {
  size: DirectionalSpacing;
  type: "margin" | "padding" | "gap";
}) {
  if (!size || typeof size !== "object") return "";
  const spacingClasses: Record<string, string> = {
    "0": "0",
    "1": "1",
    "2": "2",
    "3": "3",
    "4": "4",
    "5": "5",
    "6": "6",
    "8": "8",
    "10": "10",
    "12": "12",
    "16": "16",
    "20": "20",
    "24": "24",
    "32": "32",
    "40": "40",
    "48": "48",
    "56": "56",
    "64": "64",
  };

  const getResponsiveClasses = (
    value: number | ResponsiveValue | undefined,
    prefix: string,
  ): string[] => {
    if (!value) return [];

    const classes: string[] = [];

    if (typeof value === "number") {
      classes.push(`${prefix}-${spacingClasses[value.toString()] || value}`);
    } else {
      const { sm, md, lg, xl } = value as ResponsiveValue;

      if (sm) {
        classes.push(`sm:${prefix}-${spacingClasses[sm.toString()] || sm}`);
      }
      if (md) {
        classes.push(`md:${prefix}-${spacingClasses[md.toString()] || md}`);
      }
      if (lg) {
        classes.push(`lg:${prefix}-${spacingClasses[lg.toString()] || lg}`);
      }
      if (xl) {
        classes.push(`xl:${prefix}-${spacingClasses[xl.toString()] || xl}`);
      }
    }

    return classes;
  };

  if (typeof size === "string") {
    const prefix = type === "padding" ? "p" : type === "margin" ? "m" : "gap";
    return `${prefix}-${spacingClasses[size] || size}`;
  }

  if (
    !("all" in size) &&
    !("left" in size) &&
    !("right" in size) &&
    !("top" in size) &&
    !("bottom" in size) &&
    !("vertical" in size) &&
    !("horizontal" in size)
  ) {
    const prefix = type === "padding" ? "p" : type === "margin" ? "m" : "gap";
    return getResponsiveClasses(size as ResponsiveValue, prefix).join(" ");
  }

  const classes: string[] = [];
  const prefix = type === "padding" ? "p" : type === "margin" ? "m" : "gap";
  const directionalSize = size as DirectionalSpacing;

  if (directionalSize.all) {
    classes.push(...getResponsiveClasses(directionalSize.all, prefix));
  }

  if (directionalSize.all) {
    classes.push(...getResponsiveClasses(directionalSize.all, `${prefix}`));
  }
  if (directionalSize.left) {
    classes.push(...getResponsiveClasses(directionalSize.left, `${prefix}l`));
  }
  if (directionalSize.right) {
    classes.push(...getResponsiveClasses(directionalSize.right, `${prefix}r`));
  }
  if (directionalSize.top) {
    classes.push(...getResponsiveClasses(directionalSize.top, `${prefix}t`));
  }
  if (directionalSize.bottom) {
    classes.push(...getResponsiveClasses(directionalSize.bottom, `${prefix}b`));
  }
  if (directionalSize.vertical) {
    classes.push(
      ...getResponsiveClasses(directionalSize.vertical, `${prefix}y`),
    );
  }
  if (directionalSize.horizontal) {
    classes.push(
      ...getResponsiveClasses(directionalSize.horizontal, `${prefix}x`),
    );
  }

  return classes.join(" ");
}

export function getResponsiveClasses({
  normal,
  sm,
  md,
  lg,
  xl,
}: {
  normal?: string | string[];
  sm?: string | string[];
  md?: string | string[];
  lg?: string | string[];
  xl?: string | string[];
}) {
  const classes: string[] = [];

  if (normal) {
    const normalClasses = Array.isArray(normal) ? normal : [normal];
    classes.push(...normalClasses);
  }

  if (sm) {
    const smClasses = Array.isArray(sm) ? sm : [sm];
    classes.push(...smClasses.map((c) => `sm:${c}`));
  }

  if (md) {
    const mdClasses = Array.isArray(md) ? md : [md];
    classes.push(...mdClasses.map((c) => `md:${c}`));
  }

  if (lg) {
    const lgClasses = Array.isArray(lg) ? lg : [lg];
    classes.push(...lgClasses.map((c) => `lg:${c}`));
  }

  if (xl) {
    const xlClasses = Array.isArray(xl) ? xl : [xl];
    classes.push(...xlClasses.map((c) => `xl:${c}`));
  }

  return classes.join(" ");
}

export function getCssVariableValue(value: string) {
  return `var(--${value})`;
}

export function formatPhoneNumber(
  phoneNumber: string,
  formatMode:
    | "+XX (XXX) XXX-XXXX"
    | "(XXX) XXX-XXXX"
    | "XXX-XXXX"
    | "+XXX (XXX) XXX-XXXX"
    | "+(XXX)-XXXXXXXXX",
) {
  const cleaned = phoneNumber.replace(/[^0-9]/g, "");

  if (formatMode === "+(XXX)-XXXXXXXXX") {
    if (cleaned.length >= 12) {
      const countryCode = cleaned.slice(0, 3);
      const localNumber = cleaned.slice(3);
      return `+(${countryCode})-${localNumber}`;
    }
  } else if (formatMode === "+XX (XXX) XXX-XXXX") {
    // For 10-digit numbers with country code
    if (cleaned.length >= 12) {
      // Has country code
      const countryCode = cleaned.slice(0, cleaned.length - 10);
      const number = cleaned.slice(-10);
      return `+${countryCode} (${number.slice(0, 3)}) ${number.slice(3, 6)}-${number.slice(6)}`;
    } else if (cleaned.length === 10) {
      // Just number, assume US
      return `+1 (${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    }
  } else if (formatMode === "+XXX (XXX) XXX-XXXX") {
    // Specifically for Saudi Arabia (+966)
    if (cleaned.length === 13 && cleaned.startsWith("966")) {
      const localNumber = cleaned.slice(3);
      return `+966 (${localNumber.slice(0, 2)}) ${localNumber.slice(2, 5)}-${localNumber.slice(5)}`;
    }
  } else if (formatMode === "(XXX) XXX-XXXX") {
    if (cleaned.length >= 10) {
      const number = cleaned.slice(-10);
      return `(${number.slice(0, 3)}) ${number.slice(3, 6)}-${number.slice(6)}`;
    }
  } else {
    if (cleaned.length >= 10) {
      const number = cleaned.slice(-10);
      return `${number.slice(0, 3)}-${number.slice(3, 6)}-${number.slice(6)}`;
    }
  }

  return phoneNumber;
}

export function getPeriodTime(period: string, isRtl: boolean = false) {
  const [startHour, startMinute] = period.split(":");
  const startDate = new Date(
    0,
    0,
    0,
    parseInt(startHour),
    parseInt(startMinute),
  );
  const t =
    parseInt(startHour) < 12 ? (isRtl ? "ص" : "AM") : isRtl ? "م" : "PM";
  return dayjs(startDate).format("hh:mm") + " " + t;
}

export function getDayName(
  day: DayOfWeek,
  short: boolean = false,
  isRtl: boolean = false,
) {
  const arDays = {
    0: "الاحد",
    1: "الاثنين",
    2: "الثلاثاء",
    3: "الاربعاء",
    4: "الخميس",
    5: "الجمعة",
    6: "السبت",
  };
  const arShortDays = {
    0: "أحد",
    1: "إثن",
    2: "ثلا",
    3: "أربع",
    4: "خمي",
    5: "جمع",
    6: "سبت",
  };
  const enDays = {
    0: "Sunday",
    1: "Monday",
    2: "Tuesday",
    3: "Wednesday",
    4: "Thursday",
    5: "Friday",
    6: "Saturday",
  };
  const enShortDays = {
    0: "Sun",
    1: "Mon",
    2: "Tue",
    3: "Wed",
    4: "Thu",
    5: "Fri",
    6: "Sat",
  };
  return isRtl
    ? short
      ? arShortDays[day]
      : arDays[day]
    : short
      ? enShortDays[day]
      : enDays[day];
}

export const isRTL = (locale: string) => {
  return locale === "ar";
};
